/**
 * Auth0 session initialization for internal (@tempus.com) users.
 *
 * Internal users authenticate directly with Okta (bypassing the Auth0 IdP daisy-chain). Before
 * navigating to a downstream Auth0-only app, account-ui redirects the user through Auth0's
 * /authorize endpoint with `connection=<okta-enterprise>` so Auth0 federates back to Okta (which
 * already has an active session), establishing an Auth0 SSO session cookie. The Auth0 authorization
 * code returned to the callback is intentionally discarded — the only purpose is the session cookie.
 */
import envConfig from '../config/config';
import { getOauthBrowsingContextOrigin } from './okta-config';

const INTERNAL_EMAIL_DOMAIN = '@tempus.com';

export const AUTH0_SESSION_CALLBACK_PATH = '/callback/auth0';

const AUTH0_SESSION_INIT_STATE_STORAGE_NAME = 'auth0_session_init_state';

export function isInternalUser(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

export function isAuth0SessionInitConfigured() {
  const { sessionInitDomain, sessionInitClientId, sessionInitConnection } = envConfig.auth0 || {};
  return !!(sessionInitDomain && sessionInitClientId && sessionInitConnection);
}

export function needsAuth0SessionInit(email) {
  if (sessionStorage.getItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME)) {
    return false;
  }
  return isInternalUser(email) && isAuth0SessionInitConfigured();
}

export function buildAuth0SessionInitAuthorizeUrl(email, state) {
  const { sessionInitDomain, sessionInitClientId, sessionInitConnection } = envConfig.auth0 || {};
  const redirectUri = `${getOauthBrowsingContextOrigin()}${AUTH0_SESSION_CALLBACK_PATH}`;

  const params = new URLSearchParams({
    client_id: sessionInitClientId,
    connection: sessionInitConnection,
    login_hint: email,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
    state,
  });

  return `https://${sessionInitDomain}/authorize?${params.toString()}`;
}

export function startAuth0SessionInit(email) {
  const state = generateState();
  sessionStorage.setItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME, state);
  const url = buildAuth0SessionInitAuthorizeUrl(email, state);
  window.location.assign(url);
}

export function verifyAndClearAuth0SessionInitState(returnedState) {
  const expected = sessionStorage.getItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME);
  sessionStorage.removeItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME);
  return !!(expected && expected === returnedState);
}

function generateState() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}
